import * as _ from 'lodash';
import { Board, BoardItem } from '../monday-api/api';
import { handlersByType, ColumnValueWrapped } from './column_handlers';
import { parseJson } from './converters';
import BoardRelationColumnHandler from './column_handlers/board-relation_column_handler';
import type { ConvertValueOptions } from './types';

export type EngineBoardItem = Omit<BoardItem, 'column_values' | 'subitems'> & {
  values: ColumnValueWrapped[];
  subitems?: EngineBoardItem[];
};

export function createWrappedColumnValues(
  board: Board,
  boardItem: BoardItem,
  convertValueOptions?: ConvertValueOptions,
): ColumnValueWrapped[] {
  return boardItem.column_values.flatMap((columnValue) => {
    const handler = handlersByType[columnValue.type];

    if (!handler) {
      if (columnValue.type !== 'subtasks') {
        // console.log(`Unknown column type: ${columnValue.type}`);
      }
      return [];
    }

    try {
      // TODO: fix typing
      const value = handler.convertValueFromMondayToEngine(columnValue as any, boardItem, board, convertValueOptions);

      const columnValueWrapped: ColumnValueWrapped = {
        column_id: columnValue.id,
        column_type: handler.type,
        value_type: 'success',
        value,
      } as ColumnValueWrapped;

      return [columnValueWrapped];
    } catch (err) {
      const columnValueWrapped: ColumnValueWrapped = {
        column_id: columnValue.id,
        column_type: handler.type,
        value_type: 'error',
        value: {
          value: `ERROR: ${(err as Error)?.message}`, // TODO: solve this in a better way
        },
      } as ColumnValueWrapped;

      return [columnValueWrapped];
    }
  });
}

export function boardItemMondayToBoardItemEngine(
  board: Board,
  boardItem: BoardItem,
  subboard?: Board,
  convertValueOptions?: ConvertValueOptions,
): EngineBoardItem {
  const columnValuesWrapped = createWrappedColumnValues(board, boardItem, convertValueOptions);

  const itm: EngineBoardItem = {
    ..._.omit(boardItem, 'column_values', 'subitems'),
    values: columnValuesWrapped,
  };

  if (subboard && boardItem.subitems) {
    itm.subitems = (boardItem.subitems || []).map((subitem) =>
      boardItemMondayToBoardItemEngine(subboard, subitem, undefined, convertValueOptions),
    );
  }

  return itm;
}

export function getSubboardId(board: Board): number | null {
  const subtasksColumn = board.columns.find(({ type }) => type === 'subtasks');

  if (!subtasksColumn) {
    return null;
  }

  const settings = parseJson(subtasksColumn.settings_str);

  if (Array.isArray(settings.boardIds) && settings.boardIds.length) {
    return settings.boardIds[0];
  }

  return null;
}

export function getConnectedBoardIds(board: Board) {
  if (!board.columns) {
    return [];
  }

  return board.columns.flatMap((column) => {
    if (!BoardRelationColumnHandler.convertSettingsToEngine || column.type !== 'board_relation') {
      return [];
    }

    const settings = BoardRelationColumnHandler.convertSettingsToEngine(column.settings_str);

    return settings.boardIds;
  });
}
